<template>
  <section class="tcv" :aria-labelledby="`${uid}-title`">
    <div class="tcv-head">
      <span :id="`${uid}-title`" class="tcv-title">
        Fields
        <span v-if="variables.length" class="tcv-count">{{ variables.length }}</span>
      </span>
      <div class="tcv-head-actions">
        <button
          v-if="variables.length"
          type="button"
          class="tcv-add-btn"
          :disabled="atLimit"
          @click="addVariable()"
        >
          <i class="pi pi-plus" aria-hidden="true"></i>
          <span>Add field</span>
        </button>
        <div ref="dateMenu" class="tcv-date">
          <button
            type="button"
            class="tcv-add-btn tcv-add-btn--quiet"
            :disabled="atLimit"
            aria-haspopup="menu"
            :aria-expanded="String(showDateMenu)"
            :aria-controls="`${uid}-date-menu`"
            @click="toggleDateMenu"
            @keydown.esc.prevent="closeDateMenu"
          >
            <i class="pi pi-calendar" aria-hidden="true"></i>
            <span>Add date</span>
            <i class="pi pi-chevron-down tcv-add-caret" aria-hidden="true"></i>
          </button>
          <Transition name="tcv-pop">
            <div
              v-if="showDateMenu"
              :id="`${uid}-date-menu`"
              class="tcv-date-menu"
              role="menu"
              aria-label="Add date"
              @keydown.esc.prevent="closeDateMenu"
            >
              <button
                v-for="option in dateSources"
                :key="option.value"
                type="button"
                role="menuitem"
                class="tcv-date-option"
                @click="addVariable(option.value)"
              >
                {{ option.label }}
              </button>
            </div>
          </Transition>
        </div>
      </div>
    </div>

    <button v-if="!variables.length" type="button" class="tcv-empty" @click="addVariable()">
      <span class="tcv-empty-icon" aria-hidden="true"><i class="pi pi-plus"></i></span>
      <span class="tcv-empty-title">Add a field</span>
    </button>

    <template v-else>
      <div class="tcv-columns" aria-hidden="true">
        <span></span>
        <span>Label</span>
        <span>Code</span>
        <span class="tcv-columns-required">Required</span>
        <span></span>
      </div>

      <TransitionGroup tag="ul" name="tcv-row" class="tcv-list">
        <li
          v-for="(variable, index) in variables"
          :key="variable.id"
          class="tcv-row"
          :class="{
            'tcv-row--dragging': drag && drag.id === variable.id,
            'tcv-row--confirming': confirmingRemoveId === variable.id,
          }"
        >
          <div class="tcv-row-main">
            <button
              :id="handleId(variable)"
              type="button"
              class="tcv-handle"
              :aria-label="`Reorder ${variable.label || 'field'}, position ${index + 1} of ${variables.length}. Use the arrow keys to move it.`"
              title="Drag to reorder"
              @pointerdown="startDrag(variable, $event)"
              @keydown.up.prevent="moveBy(index, -1)"
              @keydown.down.prevent="moveBy(index, 1)"
            >
              <svg viewBox="0 0 10 16" width="10" height="16" aria-hidden="true">
                <circle cx="2.5" cy="3" r="1.3" />
                <circle cx="7.5" cy="3" r="1.3" />
                <circle cx="2.5" cy="8" r="1.3" />
                <circle cx="7.5" cy="8" r="1.3" />
                <circle cx="2.5" cy="13" r="1.3" />
                <circle cx="7.5" cy="13" r="1.3" />
              </svg>
            </button>

            <!-- Label -->
            <div class="tcv-cell tcv-cell--label">
              <label class="tcv-cell-label" :for="labelId(variable)">Label</label>
              <input
                :id="labelId(variable)"
                type="text"
                class="form-control tcv-input"
                :class="{ 'tcv-input--error': labelError(variable) }"
                :value="variable.label"
                :maxlength="labelMaxLength"
                placeholder="e.g. Pledge amount"
                autocomplete="off"
                @input="onLabelInput(variable, $event)"
                @blur="onLabelBlur(variable)"
                @keydown.enter.prevent="focusInput(codeId(variable))"
              />
              <span v-if="labelError(variable)" class="tcv-message tcv-message--error">
                {{ labelError(variable) }}
              </span>
              <span v-else-if="automaticSource(variable)" class="tcv-message tcv-message--auto">
                <i class="pi pi-calendar" aria-hidden="true"></i>
                {{ automaticSource(variable).label }}
              </span>
            </div>

            <!-- Code -->
            <div class="tcv-cell tcv-cell--code">
              <label class="tcv-cell-label" :for="codeId(variable)">Code</label>
              <div
                class="tcv-code"
                :class="{ 'tcv-code--error': codeError(variable) }"
                @mousedown="focusCodeInput(variable, $event)"
              >
                <span class="tcv-code-brace" aria-hidden="true">{</span>
                <input
                  :id="codeId(variable)"
                  type="text"
                  class="tcv-code-input"
                  :style="{ width: codeInputWidth(variable) }"
                  :value="variable.code"
                  :maxlength="codeMaxLength"
                  placeholder="Fills in from the label"
                  spellcheck="false"
                  autocomplete="off"
                  autocapitalize="off"
                  :aria-describedby="`${codeId(variable)}-note`"
                  @input="onCodeInput(variable, $event)"
                  @blur="onCodeBlur(variable)"
                  @keydown.enter.prevent="onCodeEnter(index)"
                />
                <span class="tcv-code-brace" aria-hidden="true">}</span>
                <span class="tcv-code-fill" aria-hidden="true"></span>
                <span
                  v-if="isLinked(variable) && variable.code"
                  class="tcv-code-badge"
                  title="Made from the label as you type it. Edit it to choose your own."
                >From label</span>
                <button
                  v-else-if="canRelink(variable)"
                  type="button"
                  class="tcv-code-relink"
                  :title="`Generate from the label: {${suggestedCode(variable)}}`"
                  :aria-label="`Generate the code from the label: ${suggestedCode(variable)}`"
                  @click="relink(variable)"
                >
                  <i class="pi pi-sync" aria-hidden="true"></i>
                </button>
              </div>
              <span :id="`${codeId(variable)}-note`" class="tcv-note-slot">
                <span v-if="codeError(variable)" class="tcv-message tcv-message--error">
                  {{ codeError(variable) }}
                </span>
                <template v-else-if="codeNote(variable) === 'renamed'">
                  <span class="tcv-message tcv-message--info">
                    <i class="pi pi-info-circle" aria-hidden="true"></i>
                    Renamed from <code>{{ '{' + savedCodeById[variable.id] + '}' }}</code>
                  </span>
                </template>
                <span v-else-if="codeNote(variable) === 'placed'" class="tcv-message tcv-message--ok">
                  <i class="pi pi-check-circle" aria-hidden="true"></i>
                  In the content
                </span>
              </span>
            </div>

            <!-- Required: a typed field's choice; one that fills itself in has nothing to require -->
            <div class="tcv-cell tcv-cell--required">
              <span :id="`${labelId(variable)}-required`" class="tcv-cell-label">Required</span>
              <span
                v-if="automaticSource(variable)"
                class="tcv-required-auto"
                title="Fills itself in"
              >
                <i class="pi pi-calendar" aria-hidden="true"></i>
                <span class="tcv-required-auto-text">Fills itself in</span>
              </span>
              <button
                v-else
                type="button"
                class="tcv-switch"
                :class="{ 'tcv-switch--on': variable.required }"
                role="switch"
                :aria-checked="String(variable.required)"
                :aria-label="`Required: ${variable.label || 'this field'}`"
                @click="updateVariable(variable.id, { required: !variable.required })"
              >
                <span class="tcv-switch-thumb"></span>
              </button>
            </div>

            <!-- Remove -->
            <div class="tcv-cell tcv-cell--actions">
              <button
                type="button"
                class="tcv-icon-btn"
                :aria-label="`Remove ${variable.label || 'field'}`"
                title="Remove field"
                @click="requestRemove(variable)"
              >
                <i class="pi pi-trash" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <Transition name="tcv-confirm">
            <div v-if="confirmingRemoveId === variable.id" class="tcv-confirm" role="alertdialog" :aria-label="`Remove ${variable.label || 'field'}?`">
              <i class="pi pi-exclamation-triangle tcv-confirm-icon" aria-hidden="true"></i>
              <span class="tcv-confirm-text">
                Also removes <code>{{ '{' + placedCode(variable) + '}' }}</code> from the content.
              </span>
              <span class="tcv-confirm-actions">
                <button type="button" class="tcv-confirm-btn" @click="cancelRemove(variable)">Keep</button>
                <button
                  :id="`${labelId(variable)}-remove`"
                  type="button"
                  class="tcv-confirm-btn tcv-confirm-btn--danger"
                  @click="removeVariable(variable)"
                >
                  Remove
                </button>
              </span>
            </div>
          </Transition>
        </li>
      </TransitionGroup>

      <p v-if="atLimit" class="tcv-limit">
        <i class="pi pi-info-circle" aria-hidden="true"></i>
        A template can have up to {{ maxCount }} fields.
      </p>
    </template>

    <span class="tcv-sr-only" aria-live="polite">{{ announcement }}</span>
  </section>
</template>

<script>
import {
  AUTOMATIC_VARIABLE_SOURCES,
  CUSTOM_VARIABLE_CODE_MAX_LENGTH,
  CUSTOM_VARIABLE_LABEL_MAX_LENGTH,
  CUSTOM_VARIABLE_MAX_COUNT,
  automaticSourceOf,
  createVariableId,
  removeTemplateToken,
  renameTemplateTokens,
  suggestVariableCode,
  templateHasToken,
  toVariableCode,
  validateTemplateVariables,
} from '@/utils/donation_template';

/**
 * A donation template's fields — its only placeholders: a label, the {code}
 * its content places, and where the value comes from — typed into each
 * donation (which the form may require), or one of the donation's dates.
 *
 * A new field's code follows its label in camelCase until it is edited by
 * hand. The content is kept in step with the codes: when a field's code
 * changes, the placeholders the content already holds for it are renamed as
 * the field is left (and before a save — `commit`), and removing a field takes
 * its placeholders out.
 */
export default {
  name: 'Template_Custom_Variables',
  props: {
    // The fields, in the order the donation form shows them (v-model).
    modelValue: {
      type: Array,
      default: () => [],
    },
    // The template's content, kept in step with the codes (v-model:content).
    content: {
      type: String,
      default: '',
    },
    // The fields as the template was last saved — a renamed one says so.
    savedVariables: {
      type: Array,
      default: () => [],
    },
    // Every problem shows, not only those in fields already left (a save was tried).
    showAllErrors: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'update:content'],
  data() {
    return {
      uid: `tcv-${Math.random().toString(36).slice(2, 8)}`,
      labelMaxLength: CUSTOM_VARIABLE_LABEL_MAX_LENGTH,
      codeMaxLength: CUSTOM_VARIABLE_CODE_MAX_LENGTH,
      maxCount: CUSTOM_VARIABLE_MAX_COUNT,
      // Per field, by id: whether its code still follows its label (`linked`),
      // and the code the content places it under (`placed`).
      meta: {},
      // Per field, by id: which inputs were left, so a problem shows once.
      touched: {},
      confirmingRemoveId: null,
      drag: null,
      announcement: '',
      dateSources: AUTOMATIC_VARIABLE_SOURCES,
      showDateMenu: false,
    };
  },
  computed: {
    variables() {
      return this.modelValue;
    },
    errors() {
      return validateTemplateVariables(this.variables);
    },
    atLimit() {
      return this.variables.length >= CUSTOM_VARIABLE_MAX_COUNT;
    },
    savedCodeById() {
      const codes = {};
      this.savedVariables.forEach((variable) => {
        codes[variable.id] = variable.code;
      });
      return codes;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(variables) {
        (variables || []).forEach((variable) => {
          // A field that came with the template: its code was chosen, and the
          // content places it under that code.
          if (!this.meta[variable.id]) this.meta[variable.id] = { linked: false, placed: variable.code };
        });
      },
    },
  },
  methods: {
    /* ------------------------------- ids ------------------------------- */
    labelId(variable) {
      return `${this.uid}-label-${variable.id}`;
    },
    codeId(variable) {
      return `${this.uid}-code-${variable.id}`;
    },
    handleId(variable) {
      return `${this.uid}-handle-${variable.id}`;
    },
    focusInput(id, { scroll = false } = {}) {
      const element = document.getElementById(id);
      if (!element) return;
      element.focus({ preventScroll: true });
      if (scroll) element.scrollIntoView({ block: 'center', behavior: 'smooth' });
    },
    announce(message) {
      // Cleared first, so the same words twice are still read out.
      this.announcement = '';
      this.$nextTick(() => {
        this.announcement = message;
      });
    },

    /* ------------------------------ state ------------------------------ */
    emitVariables(variables) {
      this.$emit('update:modelValue', variables);
    },
    updateVariable(id, changes) {
      this.emitVariables(this.variables.map((variable) => (variable.id === id ? { ...variable, ...changes } : variable)));
    },
    otherCodes(id) {
      return this.variables.filter((variable) => variable.id !== id && variable.code).map((variable) => variable.code);
    },
    isLinked(variable) {
      return Boolean(this.meta[variable.id]?.linked);
    },
    isTouched(id, field) {
      return Boolean(this.touched[id]?.[field]);
    },
    touch(id, field) {
      this.touched[id] = { ...this.touched[id], [field]: true };
    },
    placedCode(variable) {
      return this.meta[variable.id]?.placed || '';
    },
    // The date a field fills itself in with; null for one typed into the form.
    automaticSource(variable) {
      return automaticSourceOf(variable.source);
    },
    isPlaced(variable) {
      return templateHasToken(this.content, this.placedCode(variable) || variable.code);
    },

    /* ---------------------------- problems ----------------------------- */
    labelError(variable) {
      const error = this.errors[variable.id]?.label;
      if (!error) return '';
      return this.showAllErrors || this.isTouched(variable.id, 'label') ? error : '';
    },
    codeError(variable) {
      const error = this.errors[variable.id]?.code;
      if (!error) return '';
      // A clash or a stray character shows as it happens.
      if (variable.code) return error;
      // An empty code that follows the label: the label's own message says it.
      if (this.isLinked(variable)) return '';
      return this.showAllErrors || this.isTouched(variable.id, 'code') ? error : '';
    },
    codeNote(variable) {
      if (!variable.code || this.errors[variable.id]?.code) return null;
      const saved = this.savedCodeById[variable.id];
      if (saved && saved !== variable.code) return 'renamed';
      return this.isPlaced(variable) ? 'placed' : null;
    },

    /* ------------------------------ editing ----------------------------- */
    // A field typed into the form ('form'), or — named after it to start with —
    // one that fills itself in with one of the donation's dates.
    addVariable(source = 'form') {
      if (this.atLimit) return;
      this.closeDateMenu();
      const date = automaticSourceOf(source);
      const label = date ? date.fieldLabel : '';
      const variable = {
        id: createVariableId(),
        label,
        code: suggestVariableCode(label, this.otherCodes(null)),
        required: false,
        source: date ? date.value : 'form',
      };
      this.meta[variable.id] = { linked: true, placed: '' };
      this.emitVariables([...this.variables, variable]);
      this.announce(date ? `${date.label} field added.` : `Field ${this.variables.length + 1} added.`);
      this.$nextTick(() => this.focusInput(this.labelId(variable), { scroll: true }));
    },
    toggleDateMenu() {
      this.showDateMenu = !this.showDateMenu;
      if (this.showDateMenu) {
        this.$nextTick(() => this.$refs.dateMenu?.querySelector('.tcv-date-option')?.focus());
      }
    },
    closeDateMenu() {
      this.showDateMenu = false;
    },
    // A press anywhere outside the date menu closes it.
    onDocumentPointerDown(event) {
      if (this.showDateMenu && !this.$refs.dateMenu?.contains(event.target)) this.closeDateMenu();
    },
    onLabelInput(variable, event) {
      const label = event.target.value;
      const changes = { label };
      if (this.isLinked(variable)) changes.code = suggestVariableCode(label, this.otherCodes(variable.id));
      this.updateVariable(variable.id, changes);
    },
    onLabelBlur(variable) {
      this.touch(variable.id, 'label');
      this.commit();
    },
    // Only letters and digits make a code: anything else typed or pasted —
    // spaces, the braces themselves — is dropped where it lands, and the caret
    // stays where it was.
    onCodeInput(variable, event) {
      const input = event.target;
      const raw = input.value;
      const code = raw.replace(/[^A-Za-z0-9]/g, '');
      if (code !== raw) {
        const caret = raw.slice(0, input.selectionStart ?? raw.length).replace(/[^A-Za-z0-9]/g, '').length;
        input.value = code;
        input.setSelectionRange(caret, caret);
      }
      if (this.meta[variable.id]) this.meta[variable.id].linked = false;
      this.updateVariable(variable.id, { code });
    },
    onCodeBlur(variable) {
      this.touch(variable.id, 'code');
      this.commit();
    },
    // The input is only as wide as its code, so the closing brace sits right
    // after it ("ch" is exact in a monospaced face); empty, it makes room for
    // its hint.
    codeInputWidth(variable) {
      return variable.code ? `${variable.code.length + 0.6}ch` : '24ch';
    },
    // Anywhere in the code box puts the caret in the code, at its end.
    focusCodeInput(variable, event) {
      if (event.target.closest('input, button')) return;
      event.preventDefault();
      const input = document.getElementById(this.codeId(variable));
      if (!input) return;
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    },
    onCodeEnter(index) {
      const next = this.variables[index + 1];
      if (next) this.focusInput(this.labelId(next));
      else document.activeElement?.blur?.();
    },
    suggestedCode(variable) {
      return suggestVariableCode(variable.label, this.otherCodes(variable.id));
    },
    // Offered once the code no longer follows the label, and only when the
    // label has letters to make one from (a Hebrew label has none).
    canRelink(variable) {
      if (this.isLinked(variable) || !toVariableCode(variable.label)) return false;
      return this.suggestedCode(variable) !== variable.code;
    },
    relink(variable) {
      if (this.meta[variable.id]) this.meta[variable.id].linked = true;
      this.updateVariable(variable.id, { code: this.suggestedCode(variable) });
      this.$nextTick(() => this.commit());
    },

    /**
     * Renames the placeholders of every field whose code changed since the
     * content last placed it — all in one pass, so two fields that swapped
     * codes swap places rather than merge. A code with a problem waits.
     * Called as a field is left, and by the template modal before it saves.
     */
    commit() {
      const renames = {};
      const committed = [];
      this.variables.forEach((variable) => {
        const meta = this.meta[variable.id];
        if (!meta || !variable.code || this.errors[variable.id]?.code || variable.code === meta.placed) return;
        if (meta.placed) renames[meta.placed] = variable.code;
        committed.push([meta, variable.code]);
      });
      if (!committed.length) return;

      const content = renameTemplateTokens(this.content, renames);
      committed.forEach(([meta, code]) => {
        meta.placed = code;
      });
      if (content !== this.content) this.$emit('update:content', content);
    },

    /* ------------------------------ removing ---------------------------- */
    requestRemove(variable) {
      if (this.placedCode(variable) && this.isPlaced(variable)) {
        this.confirmingRemoveId = variable.id;
        this.$nextTick(() => this.focusInput(`${this.labelId(variable)}-remove`));
        return;
      }
      this.removeVariable(variable);
    },
    cancelRemove(variable) {
      this.confirmingRemoveId = null;
      this.$nextTick(() => this.focusInput(this.labelId(variable)));
    },
    removeVariable(variable) {
      const index = this.variables.findIndex((entry) => entry.id === variable.id);
      const placed = this.placedCode(variable);
      if (placed && templateHasToken(this.content, placed)) {
        this.$emit('update:content', removeTemplateToken(this.content, placed));
      }
      this.confirmingRemoveId = null;
      delete this.meta[variable.id];
      delete this.touched[variable.id];

      const remaining = this.variables.filter((entry) => entry.id !== variable.id);
      this.emitVariables(remaining);
      this.announce(`${variable.label || 'Field'} removed.`);

      // Focus stays in the list: the field that took its place, else the one
      // before it.
      this.$nextTick(() => {
        const neighbour = remaining[index] || remaining[index - 1];
        if (neighbour) this.focusInput(this.labelId(neighbour));
      });
    },

    /* ----------------------------- reordering --------------------------- */
    moveVariable(from, to) {
      if (from === to || to < 0 || to >= this.variables.length) return;
      const next = [...this.variables];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      this.emitVariables(next);
    },
    moveBy(index, delta) {
      const to = index + delta;
      if (to < 0 || to >= this.variables.length) return;
      const variable = this.variables[index];
      this.moveVariable(index, to);
      this.announce(`${variable.label || 'Field'} moved to position ${to + 1} of ${this.variables.length}.`);
      this.$nextTick(() => this.focusInput(this.handleId(variable)));
    },
    // Pointer dragging works for mouse, pen and touch alike. The rows trade
    // places as the pointer passes the middle of the next one, measured on
    // their layout (offsetTop) — not where a move animation has them drawn —
    // so a row never flickers back and forth mid-animation.
    startDrag(variable, event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      event.preventDefault();
      this.drag = { id: variable.id, pointerId: event.pointerId };
      document.body.classList.add('tcv-dragging');
      window.addEventListener('pointermove', this.onDragMove);
      window.addEventListener('pointerup', this.endDrag);
      window.addEventListener('pointercancel', this.endDrag);
    },
    onDragMove(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      const list = this.$el.querySelector('.tcv-list');
      if (!list) return;
      const from = this.variables.findIndex((variable) => variable.id === this.drag.id);
      if (from < 0) return;

      const y = event.clientY - list.getBoundingClientRect().top;
      let to = from;
      Array.from(list.children).forEach((row, index) => {
        const middle = row.offsetTop + row.offsetHeight / 2;
        if (index < from && y < middle) to = Math.min(to, index);
        if (index > from && y > middle) to = Math.max(to, index);
      });
      this.moveVariable(from, to);
    },
    endDrag() {
      if (!this.drag) return;
      const index = this.variables.findIndex((variable) => variable.id === this.drag.id);
      if (index >= 0) {
        this.announce(`${this.variables[index].label || 'Field'} is at position ${index + 1} of ${this.variables.length}.`);
      }
      this.drag = null;
      this.stopDragListeners();
    },
    stopDragListeners() {
      document.body.classList.remove('tcv-dragging');
      window.removeEventListener('pointermove', this.onDragMove);
      window.removeEventListener('pointerup', this.endDrag);
      window.removeEventListener('pointercancel', this.endDrag);
    },
  },
  mounted() {
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
  },
  beforeUnmount() {
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    this.stopDragListeners();
  },
};
</script>

<style>
/* While a field is dragged, the whole page shows the grabbing hand and no
   text gets selected along the way. */
body.tcv-dragging,
body.tcv-dragging * {
  cursor: grabbing !important;
  user-select: none !important;
}
</style>

<style scoped>
.tcv {
  --tcv-mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  margin-bottom: 18px;
}

/* ------------------------------- head ------------------------------- */
.tcv-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.tcv-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--color-text-primary);
}

.tcv-count {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-pill);
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
  font-size: 0.7rem;
  font-weight: 600;
}

.tcv-add-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border: 1px solid var(--color-primary-border);
  border-radius: var(--radius-md);
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}

.tcv-add-btn .pi {
  font-size: 0.7rem;
}

.tcv-add-btn:hover:not(:disabled) {
  background: var(--color-primary-soft-2);
  border-color: var(--color-primary);
}

.tcv-add-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.tcv-head-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* "Add date": second to "Add field", so drawn without the fill. */
.tcv-add-btn--quiet {
  border-color: var(--color-border);
  background: var(--color-bg-canvas);
  color: var(--color-text-secondary);
}

.tcv-add-btn--quiet:hover:not(:disabled) {
  border-color: var(--color-primary-border);
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
}

.tcv-add-btn .tcv-add-caret {
  font-size: 0.58rem;
  opacity: 0.8;
}

/* -------------------------- add-date menu --------------------------- */
.tcv-date {
  position: relative;
}

.tcv-date-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  width: 200px;
  padding: 6px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-bg-canvas);
  box-shadow: var(--shadow-lg);
}

.tcv-date-option {
  width: 100%;
  display: block;
  padding: 8px 10px;
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--color-text-primary);
  text-align: left;
  cursor: pointer;
  transition: background-color .12s ease;
}

.tcv-date-option:hover,
.tcv-date-option:focus-visible {
  outline: none;
  background: var(--color-primary-soft);
}

.tcv-pop-enter-active,
.tcv-pop-leave-active {
  transition: opacity .14s ease, transform .14s ease;
}

.tcv-pop-enter-from,
.tcv-pop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* ------------------------------- empty ------------------------------ */
.tcv-empty {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-lg);
  background: var(--color-bg-soft);
  text-align: left;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}

.tcv-empty:hover,
.tcv-empty:focus-visible {
  border-color: var(--color-primary);
  background: var(--color-primary-soft);
}

.tcv-empty-icon {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  background: var(--color-bg-canvas);
  border: 1px solid var(--color-primary-border);
  color: var(--color-primary-hover);
}

.tcv-empty-icon .pi {
  font-size: 0.8rem;
}

.tcv-empty-title {
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--color-text-primary);
}

/* ------------------------------- list ------------------------------- */
.tcv-columns,
.tcv-row-main {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr) minmax(0, 1fr) 66px 32px;
  column-gap: 10px;
}

/* 13px: the rows' 12px padding plus their 1px border, so the names sit over
   their columns. */
.tcv-columns {
  padding: 0 13px 6px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.tcv-columns-required {
  text-align: center;
}

/* Block layout with each row's own margin (not a flex gap): a row on its way
   out is taken out of flow (.tcv-row-leave-active) and must keep its place
   while the rows below slide up. */
.tcv-list {
  position: relative;
  margin: 0 0 -8px;
  padding: 0;
  list-style: none;
}

.tcv-row {
  margin-bottom: 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-bg-canvas);
  box-shadow: var(--shadow-xs);
  transition: border-color .15s ease, box-shadow .15s ease;
}

.tcv-row:focus-within {
  border-color: var(--color-primary-border);
}

.tcv-row--dragging {
  border-color: var(--color-primary);
  box-shadow: var(--shadow-md);
  z-index: 1;
  position: relative;
}

.tcv-row--confirming {
  border-color: #fecaca;
}

.tcv-row-main {
  align-items: start;
  padding: 10px 12px;
}

.tcv-cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* Each column is named once in the header; the per-row names are for screen
   readers, and show again where the row stacks on a phone. */
.tcv-cell-label {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* ------------------------------ handle ------------------------------ */
.tcv-handle {
  width: 22px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-border-strong);
  fill: currentColor;
  cursor: grab;
  touch-action: none;
  transition: color .15s ease, background-color .15s ease;
}

.tcv-handle:hover,
.tcv-handle:focus-visible,
.tcv-row--dragging .tcv-handle {
  color: var(--color-text-muted);
  background: var(--color-bg-muted);
}

.tcv-handle svg {
  fill: currentColor;
}

/* ------------------------------ inputs ------------------------------ */
.tcv-input.form-control {
  height: 40px !important;
  padding: 0 12px;
  font-size: 0.86rem !important;
  color: var(--color-text-primary);
  background-color: var(--color-bg-canvas);
}

.tcv-input.form-control::placeholder {
  color: var(--color-text-muted);
  font-size: 0.84rem;
}

.tcv-input--error.form-control {
  border-color: var(--color-danger) !important;
}

/* The code: braces drawn around the input, which leaves the chrome to its
   wrapper — the global input rules (index.css / theme.css) would otherwise
   draw a second border and focus ring inside it. */
.tcv-code {
  display: flex;
  align-items: center;
  gap: 1px;
  height: 40px;
  padding: 0 6px 0 11px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-soft);
  font-family: var(--tcv-mono);
  cursor: text;
  transition: border-color .15s ease, box-shadow .15s ease, background-color .15s ease;
}

.tcv-code-fill {
  flex: 1 1 auto;
  align-self: stretch;
}

.tcv-code:focus-within {
  border-color: var(--color-primary);
  box-shadow: var(--shadow-focus);
  background: var(--color-bg-canvas);
}

.tcv-code--error,
.tcv-code--error:focus-within {
  border-color: var(--color-danger);
}

.tcv-code-brace {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: 0.86rem;
  font-weight: 500;
}

.tcv-code-input {
  flex: 0 1 auto;
  min-width: 1ch;
  max-width: 100%;
  height: 100% !important;
  padding: 0 1px;
  border: none !important;
  border-radius: 0 !important;
  background: transparent;
  color: var(--color-text-primary);
  font-family: var(--tcv-mono);
  font-size: 0.84rem !important;
  outline: none;
}

.tcv-code-input:focus {
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}

.tcv-code-input::placeholder {
  color: var(--color-text-muted);
  font-family: var(--font-sans, inherit);
  font-size: 0.78rem;
}

.tcv-code-badge {
  flex-shrink: 0;
  margin-left: 4px;
  padding: 2px 7px;
  border-radius: var(--radius-pill);
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
  font-family: var(--font-sans, inherit);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: default;
}

.tcv-code-relink {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  margin-left: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease;
}

.tcv-code-relink:hover,
.tcv-code-relink:focus-visible {
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
}

.tcv-code-relink .pi {
  font-size: 0.74rem;
}

/* ----------------------------- messages ----------------------------- */
.tcv-note-slot {
  display: block;
  min-width: 0;
}

/* Plain text flow, so a long note wraps word by word. */
.tcv-message {
  display: block;
  margin-top: 5px;
  font-size: 0.72rem;
  line-height: 1.45;
  color: var(--color-text-muted);
}

.tcv-message .pi {
  margin-right: 3px;
  font-size: 0.7rem;
  vertical-align: -1px;
}

.tcv-message code {
  padding: 0 4px;
  border-radius: 4px;
  background: var(--color-bg-muted);
  color: inherit;
  font-family: var(--tcv-mono);
  font-size: 0.7rem;
}

.tcv-message--error {
  color: var(--color-danger);
}

.tcv-message--ok {
  color: #059669;
}

.tcv-message--info {
  color: var(--color-primary-hover);
}

.tcv-message--auto {
  color: var(--color-text-secondary);
}

/* In the Required column of a field that fills itself in. */
.tcv-required-auto {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 30px;
  height: 30px;
  border-radius: var(--radius-pill);
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  cursor: default;
}

.tcv-required-auto .pi {
  font-size: 0.74rem;
}

/* Named only where the row stacks on a phone. */
.tcv-required-auto-text {
  display: none;
}

/* ------------------------------ switch ------------------------------ */
.tcv-cell--required {
  align-items: center;
  justify-content: center;
  height: 40px;
}

.tcv-switch {
  position: relative;
  width: 38px;
  height: 22px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: var(--radius-pill);
  background: var(--color-border-strong);
  cursor: pointer;
  transition: background-color .18s ease;
}

.tcv-switch:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus);
}

.tcv-switch--on {
  background: var(--color-primary);
}

.tcv-switch-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.25);
  transition: transform .18s ease;
}

.tcv-switch--on .tcv-switch-thumb {
  transform: translateX(16px);
}

/* ------------------------------ remove ------------------------------ */
.tcv-cell--actions {
  align-items: center;
  justify-content: center;
  height: 40px;
}

.tcv-icon-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease;
}

.tcv-icon-btn:hover,
.tcv-icon-btn:focus-visible {
  background: #fef2f2;
  color: var(--color-danger);
}

.tcv-icon-btn .pi {
  font-size: 0.82rem;
}

.tcv-confirm {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-top: 1px solid #fee2e2;
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
  background: #fef2f2;
  overflow: hidden;
}

.tcv-confirm-icon {
  flex-shrink: 0;
  color: var(--color-danger);
  font-size: 0.8rem;
}

.tcv-confirm-text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.76rem;
  line-height: 1.4;
  color: #7f1d1d;
}

.tcv-confirm-text code {
  padding: 0 4px;
  border-radius: 4px;
  background: #fee2e2;
  color: inherit;
  font-family: var(--tcv-mono);
  font-size: 0.72rem;
}

.tcv-confirm-actions {
  flex-shrink: 0;
  display: inline-flex;
  gap: 6px;
}

.tcv-confirm-btn {
  height: 30px;
  padding: 0 12px;
  border: 1px solid #fecaca;
  border-radius: var(--radius-md);
  background: var(--color-bg-canvas);
  color: var(--color-text-secondary);
  font-size: 0.76rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}

.tcv-confirm-btn:hover {
  border-color: #fca5a5;
}

.tcv-confirm-btn--danger {
  border-color: var(--color-danger);
  background: var(--color-danger);
  color: #ffffff;
}

.tcv-confirm-btn--danger:hover {
  border-color: #dc2626;
  background: #dc2626;
}

.tcv-limit {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px 2px 0;
  font-size: 0.74rem;
  color: var(--color-text-muted);
}

.tcv-limit .pi {
  font-size: 0.72rem;
}

.tcv-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* ---------------------------- transitions --------------------------- */
.tcv-row-move {
  transition: transform .2s ease;
}

.tcv-row-enter-active {
  transition: opacity .2s ease, transform .2s ease;
}

.tcv-row-leave-active {
  position: absolute;
  left: 0;
  right: 0;
  transition: opacity .14s ease;
}

.tcv-row-enter-from {
  opacity: 0;
  transform: translateY(-6px);
}

.tcv-row-leave-to {
  opacity: 0;
}

.tcv-confirm-enter-active,
.tcv-confirm-leave-active {
  transition: opacity .16s ease, max-height .2s ease, padding .2s ease;
  max-height: 120px;
}

.tcv-confirm-enter-from,
.tcv-confirm-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

@media (prefers-reduced-motion: reduce) {
  .tcv-row-move,
  .tcv-row-enter-active,
  .tcv-row-leave-active,
  .tcv-confirm-enter-active,
  .tcv-confirm-leave-active,
  .tcv-pop-enter-active,
  .tcv-pop-leave-active {
    transition: none;
  }
}

/* ------------------------------ phones ------------------------------ */
@media (max-width: 640px) {
  .tcv-head {
    flex-direction: column;
    align-items: stretch;
  }

  /* On a phone the menu hangs from the row of buttons, not from "Add date",
     so it has the whole width to open into. */
  .tcv-head-actions {
    position: relative;
    justify-content: flex-start;
  }

  .tcv-date {
    position: static;
  }

  .tcv-date-menu {
    right: auto;
    left: 0;
    max-width: calc(100vw - 48px);
  }

  .tcv-required-auto {
    width: auto;
    height: auto;
    padding: 4px 10px;
  }

  .tcv-required-auto-text {
    display: inline;
    font-size: 0.74rem;
  }

  .tcv-columns {
    display: none;
  }

  /* The row stacks: handle and label, then the code, then Required. */
  .tcv-row-main {
    grid-template-columns: 22px minmax(0, 1fr) 32px;
    grid-template-areas:
      'handle label actions'
      '.      code  code'
      '.      required required';
    row-gap: 10px;
  }

  .tcv-handle {
    grid-area: handle;
    margin-top: 21px;
  }

  .tcv-cell--label {
    grid-area: label;
  }

  .tcv-cell--code {
    grid-area: code;
  }

  .tcv-cell--required {
    grid-area: required;
    flex-direction: row;
    justify-content: space-between;
    height: auto;
  }

  .tcv-cell--actions {
    grid-area: actions;
    margin-top: 21px;
  }

  .tcv-cell-label {
    position: static;
    width: auto;
    height: auto;
    overflow: visible;
    clip: auto;
    margin-bottom: 5px;
    font-size: 0.74rem;
    font-weight: 500;
    color: var(--color-text-secondary) !important;
  }

  .tcv-cell--required .tcv-cell-label {
    margin-bottom: 0;
  }

  .tcv-confirm {
    flex-wrap: wrap;
  }

  .tcv-confirm-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
